import { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext } from "react";

import { useUser } from "@/entities/Auth";
import { UserRole } from "@/shared/const/userRole";
import { handleError } from "@/shared/lib/helpers/utils";
import { AssignedWriter } from "@/shared/types";

import { getActiveReaders, updateReaderItem } from "../../utils/api";
import { useApi } from "../hooks/useApi";
import { useAlertContext } from "./alertContext";

type ContextType = {
  activeReadersLoading: boolean;
  activeReaders: AssignedWriter[];
  setActiveReaders: Dispatch<SetStateAction<AssignedWriter[]>>;
  refresh: () => void;
};

const ReadersContext = createContext({} as ContextType);

interface Props {
  children: ReactNode;
}

const Context: FC<Props> = ({ children }) => {
  const {
    loading: activeReadersLoading,
    data: activeReaders,
    setData: setActiveReaders,
    refresh,
  } = useApi<AssignedWriter[]>(getActiveReaders, []);

  return (
    <ReadersContext.Provider
      value={{
        activeReaders,
        setActiveReaders,
        activeReadersLoading,
        refresh,
      }}
    >
      {children}
    </ReadersContext.Provider>
  );
};

export const useReadersList = () => {
  const { showMsg } = useAlertContext();

  const { activeReadersLoading, activeReaders, refresh } = useContext(ReadersContext);

  const updateReader = useCallback(
    async (id, data) => {
      const { error } = await updateReaderItem(id, data as AssignedWriter);

      if (error) {
        const err = handleError(error);
        return showMsg(err.details);
      }

      await refresh();
    },
    [showMsg, refresh]
  );

  return {
    isLoading: activeReadersLoading,
    readersList: activeReaders,
    updateReader,
    refresh,
  };
};

const Wrapper: FC<Props> = ({ children }) => {
  const user = useUser();

  if (!user || user.role !== UserRole.Writer) {
    return <>{children}</>;
  }

  return <Context>{children}</Context>;
};

export default Wrapper;
